/**
 * IDM traffic (P1-T11, ported from prototype v3 initTraffic/updateTraffic).
 * Cars drive closed rectangular loops around block groups, staying in their
 * lane (left side of travel, like the prototype). Following uses the
 * intelligent-driver model; a red signal at the next intersection acts as a
 * stationary leader at the stop line. Drawn cars are thin-instance boxes
 * (body + roof + night headlights); only near cars are submitted.
 */
import layout from '../world/road-layout.json';
import { prng } from '@kota-nova/sim';
import {
  Matrix,
  Mesh,
  MeshBuilder,
  PBRMaterial,
  Quaternion,
  Vector3,
  Color3,
  Constants,
  type AbstractEngine,
  type Scene,
} from 'babylonjs';

const AVES = layout.aves_x as number[];
const STREETS = layout.streets_z as number[];
const AVE_W = layout.ave_w as number;
const STREET_W = layout.street_w as number;
const BAY_EDGE = layout.bay_edge_z as number;

interface Leg {
  ax: 'x' | 'z'; // axis the road runs along
  c: number; // road centre coordinate on the cross axis
  from: number;
  to: number;
  dir: 1 | -1;
  off: number; // lane offset from centre
  L: number;
  key: string;
  /** stop line distance along leg when its far intersection is red for us */
  stop: { at: number; inter: number } | null;
}

interface Inter {
  x: number;
  z: number;
  /** cycle offset seconds; NS green 0..12, all-red 12..14, EW 14..24 */
  phase: number;
}

interface Car {
  loop: number;
  leg: number;
  s: number;
  v: number;
  v0: number;
  len: number;
  bus: boolean;
  col: [number, number, number];
  x: number;
  z: number;
  yaw: number;
}

export interface TrafficOptions {
  simCars: number;
  drawCap: number;
  radius: number;
  shadows: boolean;
}

const A = 2.2; // IDM max accel m/s^2
const B = 3.0; // IDM comfortable braking
const S0 = 1.6; // standstill gap
const TT = 1.1; // headway

export class Traffic {
  private readonly loops: Leg[][] = [];
  private readonly inters: Inter[] = [];
  private readonly cars: Car[] = [];
  private readonly bodyMesh: Mesh;
  private readonly roofMesh: Mesh;
  private readonly lightMesh: Mesh;
  private readonly bodyBuf: Float32Array;
  private readonly roofBuf: Float32Array;
  private readonly lightBuf: Float32Array;
  private readonly bodyCol: Float32Array;
  /** legKey -> car indices sorted by s */
  private readonly lanes = new Map<string, number[]>();
  private night = 0;
  private t = 0;

  constructor(
    engine: AbstractEngine,
    scene: Scene,
    private readonly opts: TrafficOptions,
  ) {
    const r = prng(555);
    // intersections
    const interIdx = new Map<string, number>();
    for (const x of AVES)
      for (const z of STREETS) {
        interIdx.set(`${x},${z}`, this.inters.length);
        this.inters.push({ x, z, phase: r() * 24 });
      }

    // loops over adjacent road pairs (deterministic sampling like prototype)
    for (let tries = 0; this.loops.length < 48 && tries < 4000; tries++) {
      const i = Math.floor(r() * (AVES.length - 1));
      const j = i + 1 + Math.floor(r() * 2);
      const k = Math.floor(r() * (STREETS.length - 1));
      const l = k + 1 + Math.floor(r() * 2);
      if (j >= AVES.length || l >= STREETS.length) continue;
      const x0 = AVES[i] as number;
      const x1 = AVES[j] as number;
      const z0 = STREETS[k] as number;
      const z1 = STREETS[l] as number;
      if (z1 < BAY_EDGE) continue; // no blocks south of the shore yet
      const cw = r() < 0.5;
      const lane = r() < 0.5 ? 0 : 1;
      const corners = cw
        ? ([
            [x0, z0],
            [x0, z1],
            [x1, z1],
            [x1, z0],
          ] as Array<[number, number]>)
        : ([
            [x0, z0],
            [x1, z0],
            [x1, z1],
            [x0, z1],
          ] as Array<[number, number]>);
      const legs: Leg[] = [];
      for (let q = 0; q < 4; q++) {
        const [ax0, az0] = corners[q] as [number, number];
        const [bx0, bz0] = corners[(q + 1) % 4] as [number, number];
        const ax = ax0 === bx0 ? 'x' : 'z';
        const c = ax === 'x' ? ax0 : az0;
        const from = ax === 'x' ? az0 : ax0;
        const to = ax === 'x' ? bz0 : bx0;
        const dir = (Math.sign(to - from) || 1) as 1 | -1;
        const halfW = ax === 'x' ? AVE_W / 2 : STREET_W / 2;
        // left-hand traffic: lane sits to the left of travel
        const laneOff = halfW * (lane === 0 ? 0.45 : 0.75) * (ax === 'x' ? -dir : dir);
        const key = `${ax}${c}:${dir}:${lane}`;
        const farKey = ax === 'x' ? `${c},${to}` : `${to},${c}`;
        const inter = interIdx.get(farKey);
        legs.push({
          ax,
          c,
          from,
          to,
          dir,
          off: laneOff,
          L: Math.abs(to - from),
          key,
          stop:
            inter === undefined
              ? null
              : { at: Math.abs(to - from) - (ax === 'x' ? STREET_W : AVE_W) / 2 - 4, inter },
        });
      }
      this.loops.push(legs);
    }

    const palette: Array<[number, number, number]> = [
      [0.92, 0.92, 0.93],
      [0.08, 0.08, 0.09],
      [0.55, 0.57, 0.6],
      [0.6, 0.07, 0.08],
      [0.1, 0.2, 0.45],
      [0.75, 0.75, 0.78],
      [0.2, 0.33, 0.28],
      [0.85, 0.82, 0.74],
      [0.35, 0.36, 0.38],
    ];
    for (let i = 0; i < opts.simCars && this.loops.length > 0; i++) {
      const loop = Math.floor(r() * this.loops.length);
      const legs = this.loops[loop] as Leg[];
      const leg = Math.floor(r() * 4);
      const bus = r() < 0.05;
      const col = bus
        ? ([0.85, 0.2, 0.15] as [number, number, number])
        : (palette[Math.floor(r() * palette.length)] as [number, number, number]);
      this.cars.push({
        loop,
        leg,
        s: r() * (legs[leg]?.L ?? 100),
        v: 6 + r() * 6,
        v0: (bus ? 9 : 10) + r() * 5,
        len: bus ? 10.5 : 4.5,
        bus,
        col,
        x: 0,
        z: 0,
        yaw: 0,
      });
    }

    // meshes: unit boxes scaled per instance
    const bodyM = MeshBuilder.CreateBox('car_body', { width: 1.85, height: 0.75, depth: 1 }, scene);
    const roofM = MeshBuilder.CreateBox('car_roof', { width: 1.6, height: 0.5, depth: 1 }, scene);
    roofM.position.y = 0.6;
    const headM = MeshBuilder.CreateBox('car_light', { width: 0.3, height: 0.14, depth: 0.06 }, scene);
    headM.position.set(-0.55, 0.05, 0.52);
    const headM2 = headM.clone('car_light2');
    headM2.position.x = 0.55;
    const lights = Mesh.MergeMeshes([headM, headM2], true, true, undefined, false, true) as Mesh;
    lights.name = 'car_light';

    const paintMat = new PBRMaterial('carPaint', scene);
    paintMat.metallic = 0.55;
    paintMat.roughness = 0.35;
    const darkMat = new PBRMaterial('carDark', scene);
    darkMat.albedoColor = new Color3(0.05, 0.05, 0.06);
    darkMat.metallic = 0.2;
    darkMat.roughness = 0.6;
    const lightMat = new PBRMaterial('carLight', scene);
    lightMat.disableLighting = true;
    lightMat.emissiveColor = new Color3(1.4, 1.3, 1.0);
    lightMat.alphaMode = Constants.ALPHA_ADD;
    lightMat.disableDepthWrite = true;

    paintMat.albedoColor = new Color3(1, 1, 1);
    this.bodyMesh = bodyM;
    this.roofMesh = roofM;
    this.lightMesh = lights;
    for (const m of [bodyM, roofM, lights]) {
      m.isPickable = false;
      m.alwaysSelectAsActiveMesh = true;
      m.receiveShadows = false;
    }
    bodyM.material = paintMat;
    roofM.material = darkMat;
    lights.material = lightMat;

    const cap = opts.drawCap;
    this.bodyBuf = new Float32Array(cap * 16);
    this.roofBuf = new Float32Array(cap * 16);
    this.lightBuf = new Float32Array(cap * 16);
    this.bodyCol = new Float32Array(cap * 4);
    for (const m of [bodyM, roofM, lights]) m.thinInstanceCount = 1;
    bodyM.thinInstanceSetBuffer('matrix', this.bodyBuf, 16, false);
    bodyM.thinInstanceSetBuffer('color', this.bodyCol, 4, false);
    roofM.thinInstanceSetBuffer('matrix', this.roofBuf, 16, false);
    lights.thinInstanceSetBuffer('matrix', this.lightBuf, 16, false);
    void engine;
  }

  setNight(n: number): void {
    this.night = n;
    const mat = this.lightMesh.material as PBRMaterial;
    mat.alpha = Math.min(1, n * 1.6);
    this.lightMesh.setEnabled(n > 0.15);
  }

  /** Signal state: true = green to travel along NS (avenue) direction. */
  private nsGreen(inter: Inter, t: number): boolean {
    const ph = (((t + inter.phase) % 24) + 24) % 24;
    return ph < 12;
  }

  update(dtMs: number, camX: number, camZ: number): void {
    const dt = Math.min(0.05, dtMs / 1000);
    this.t += dt;
    const t = this.t;

    // lane buckets sorted by s for car-following
    this.lanes.clear();
    for (let ci = 0; ci < this.cars.length; ci++) {
      const car = this.cars[ci] as Car;
      const leg = (this.loops[car.loop] as Leg[])[car.leg] as Leg;
      let arr = this.lanes.get(leg.key);
      if (!arr) {
        arr = [];
        this.lanes.set(leg.key, arr);
      }
      arr.push(ci);
    }
    for (const arr of this.lanes.values()) {
      arr.sort((a, b) => {
        const ca = this.cars[a] as Car;
        const cb = this.cars[b] as Car;
        return ca.s - cb.s;
      });
    }

    for (let ci = 0; ci < this.cars.length; ci++) {
      const car = this.cars[ci] as Car;
      const legs = this.loops[car.loop] as Leg[];
      const leg = legs[car.leg] as Leg;
      // nearest leader ahead: same lane, or red stop line
      let gap = Infinity;
      const bucket = this.lanes.get(leg.key);
      if (bucket) {
        const pos = bucket.indexOf(ci);
        const next = bucket[(pos + 1) % bucket.length];
        if (next !== undefined && next !== ci) {
          const lead = this.cars[next] as Car;
          const wrap = lead.s <= car.s ? leg.L : 0;
          gap = Math.max(0.2, lead.s + wrap - car.s - lead.len);
        }
      }
      if (leg.stop) {
        const greenAlong =
          leg.ax === 'x'
            ? this.nsGreen(this.inters[leg.stop.inter] as Inter, t)
            : !this.nsGreen(this.inters[leg.stop.inter] as Inter, t);
        if (!greenAlong) gap = Math.min(gap, Math.max(0.2, leg.stop.at - car.s));
      }
      // IDM
      const dv = car.v - 0; // leader stationary when gap came from stop; simplification vs moving leaders is visually fine at city scale
      const sStar = S0 + Math.max(0, car.v * TT + (car.v * dv) / (2 * Math.sqrt(A * B)));
      const free = 1 - Math.pow(car.v / car.v0, 4);
      const inter2 = gap === Infinity ? 0 : -Math.pow(sStar / gap, 2);
      const acc = A * (free + inter2);
      car.v = Math.max(0, car.v + acc * dt);
      car.s += car.v * dt;
      if (car.s >= leg.L) {
        car.s -= leg.L;
        car.leg = (car.leg + 1) % 4;
      }
      // pose with corner fillet (Bezier through the intersection corner)
      const R = 9;
      const pose = (lg: Leg, s: number): [number, number, number] => {
        const p = lg.from + lg.dir * s;
        return lg.ax === 'x'
          ? [lg.c + lg.off, p, lg.dir > 0 ? 0 : Math.PI]
          : [p, lg.c + lg.off, lg.dir > 0 ? Math.PI / 2 : -Math.PI / 2];
      };
      if (car.s > leg.L - R) {
        const a = leg;
        const b = legs[(car.leg + 1) % 4] as Leg;
        const u = (car.s - (leg.L - R)) / (2 * R);
        const pa = pose(a, leg.L - R);
        const pb = pose(b, R);
        const cx = a.ax === 'x' ? a.c + a.off : b.c + b.off;
        const cz = a.ax === 'x' ? b.c + b.off : a.c + a.off;
        const iu = 1 - u;
        car.x = iu * iu * pa[0] + 2 * iu * u * cx + u * u * pb[0];
        car.z = iu * iu * pa[1] + 2 * iu * u * cz + u * u * pb[1];
        const dx = 2 * iu * (cx - pa[0]) + 2 * u * (pb[0] - cx);
        const dz = 2 * iu * (cz - pa[1]) + 2 * u * (pb[1] - cz);
        car.yaw = Math.atan2(dx, dz);
      } else {
        const p = pose(leg, car.s);
        car.x = p[0];
        car.z = p[1];
        car.yaw = p[2];
      }
    }

    // ---- submission: nearest cars within radius ----
    const R2 = this.opts.radius * this.opts.radius;
    const scored: Array<{ d2: number; c: Car }> = [];
    for (const car of this.cars) {
      const dx = car.x - camX;
      const dz = car.z - camZ;
      const d2 = dx * dx + dz * dz;
      if (d2 < R2) scored.push({ d2, c: car });
    }
    scored.sort((a, b) => a.d2 - b.d2);
    const n = Math.min(scored.length, this.opts.drawCap);
    for (let i = 0; i < n; i++) {
      const car = scored[i]?.c as Car;
      const q = Quaternion.RotationYawPitchRoll(car.yaw, 0, 0);
      const depth = car.len;
      const bodyS = new Vector3(1, 1, depth);
      const roofS = new Vector3(1, 1, depth * 0.55);
      const lightS = new Vector3(1, 1, depth * 0.35);
      Matrix.Compose(bodyS, q, new Vector3(car.x, 0.55, car.z)).copyToArray(this.bodyBuf, i * 16);
      Matrix.Compose(roofS, q, new Vector3(car.x, 0.55, car.z)).copyToArray(this.roofBuf, i * 16);
      Matrix.Compose(lightS, q, new Vector3(car.x, 0.55, car.z)).copyToArray(this.lightBuf, i * 16);
      this.bodyCol[i * 4] = car.col[0];
      this.bodyCol[i * 4 + 1] = car.col[1];
      this.bodyCol[i * 4 + 2] = car.col[2];
      this.bodyCol[i * 4 + 3] = 1;
    }
    // park unused slots (count never 0, D-16)
    for (let i = n; i < Math.min(n + 1, this.opts.drawCap); i++) {
      this.bodyBuf[i * 16 + 13] = -500;
      this.roofBuf[i * 16 + 13] = -500;
      this.lightBuf[i * 16 + 13] = -500;
    }
    const commit = (m: Mesh, _buf: Float32Array, col: boolean): void => {
      if (n > 0) {
        m.thinInstanceCount = n;
        m.setEnabled(true);
        m.thinInstanceBufferUpdated('matrix');
        if (col) m.thinInstanceBufferUpdated('color');
      } else {
        m.thinInstanceCount = 1;
        m.setEnabled(false);
      }
    };
    commit(this.bodyMesh, this.bodyBuf, true);
    commit(this.roofMesh, this.roofBuf, false);
    if (this.night > 0.15) commit(this.lightMesh, this.lightBuf, false);
    else this.lightMesh.setEnabled(false);
  }

  /** Car meshes (bodies cast on mid/high per SPEC-01 §6). */
  casters(): Mesh[] {
    return [this.bodyMesh];
  }

  stats(): { sim: number; drawn: number } {
    return { sim: this.cars.length, drawn: this.bodyMesh.thinInstanceCount };
  }
}
